/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  Breadcrumbs,
  Head,
  renderWithResolver,
  formatReviewToSchema,
} from '@cinch-nx/landing-ui-shared'
import {
  useStoryblokState,
  StoryblokImage,
  HowTo,
} from '@cinch-nx/data-storyblok'

import { Intro, ReviewSections, LatestCars } from './components'
import { Verdict } from './components/review-sections/verdict'
import { reviewSectionsData } from './components/review-sections/review-sections.data'

import { CarReviewsV2FeatureProps } from './get-static-props'

import { GalleryModal } from './components/gallery-modal'
import styles from './car-reviews-v2.module.css'
import { RelatedReviews } from './components/related-reviews'
import { useVehicleGallery } from './hooks'
import { useEffect } from 'react'
import { CtaPreFooter } from './components/cta-prefooter/cta-prefooter'
import { ProsAndCons } from './components/pros-and-cons/pros-and-cons'
import {
  getDefaultSectionImage,
  getFormattedGalleryImages,
  getGalleryImages,
} from './helpers'
import { ErrorModal } from './components/error-modal'
import { removeDuplicatesKeepPlaceholders } from './utils/remove-duplicates-images/remove-duplicates-images'
import Script from 'next/script'
import {
  BreadcrumbList,
  FAQPage,
  VideoObject,
  Review,
  Product,
  AutomotiveBusiness,
} from 'schema-dts'

export const placeholderImage: StoryblokImage = {
  alt: 'Image coming soon',
  copyright: 'cinch',
  filename:
    'https://a.storyblok.com/f/132543/400x253/4f1faa5d21/image-coming-soon.png',
  title: 'Image coming soon',
  fieldtype: null,
  focus: null,
  name: 'Image coming soon',
}

interface JsonLdScriptProps {
  schema:
    | BreadcrumbList
    | FAQPage
    | VideoObject
    | HowTo
    | Review
    | Product
    | AutomotiveBusiness
  tagId?: string
}

const JsonLdScript = ({ schema, tagId }: JsonLdScriptProps) => {
  const schemaToRender = Array.isArray(schema) ? schema[0] : schema

  return (
    <Script
      type="application/ld+json"
      data-testid="json-ld-script"
      id={tagId}
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(
          {
            '@context': 'https://schema.org',
            ...schemaToRender,
          },
          null,
          2,
        ),
      }}
    />
  )
}

export function CarReviewsV2Feature({
  story,
  generatedAt,
  preview,
  validationErrors,
  areVehiclesInStock,
}: CarReviewsV2FeatureProps) {
  const { content } = useStoryblokState({
    story,
    enableBridge: preview,
  })

  const IS_MOBILE_APP =
    typeof window !== 'undefined' &&
    window.navigator?.userAgent.includes('cinchapp')

  const { setImageIndexes } = useVehicleGallery()

  const SEO = content?.SEO?.[0] ?? {}

  const {
    make,
    model,
    verdict,
    starRating,
    introText,
    authorImage,
    authorName,
    title,
    interiorSection,
    performanceSection,
    practicalitySection,
    runningCostsSection,
    gallery,
    shouldShowLatestCars,
    relatedReviews,
    pros,
    cons,
    video,
  } = content

  const sections = [
    interiorSection[0],
    performanceSection[0],
    practicalitySection[0],
    runningCostsSection[0],
  ]

  const prosList = pros[0].ProAndConList
  const consList = cons[0].ProAndConList

  const titles = reviewSectionsData.map((item) => item.title)

  const reviewSections = sections.map((section, index) => ({
    ...section,
    title: titles[index],
  }))

  const contentStarRating = Number(starRating)
  const contentIntroText = renderWithResolver(introText)

  const verdictTitle = verdict[0]?.title || ''
  const verdictBody = renderWithResolver(verdict[0]?.body)

  const galleryImages = getGalleryImages(gallery)
  const formattedGalleryImages = getFormattedGalleryImages(
    galleryImages,
    placeholderImage,
  )
  const introImage = formattedGalleryImages?.at(0)

  const imagesWithDefaultSectionsImages = [
    ...formattedGalleryImages,
    ...sections.map((section) =>
      getDefaultSectionImage([section], placeholderImage),
    ),
  ]
  const images = removeDuplicatesKeepPlaceholders(
    imagesWithDefaultSectionsImages,
  )

  const imageCount = images?.length || 0

  const formattedSections = {
    interior: interiorSection[0],
    performance: performanceSection[0],
    practicality: practicalitySection[0],
    runningCosts: runningCostsSection[0],
  }

  const latestPublishDate = story?.published_at

  useEffect(() => {
    setImageIndexes(images, formattedSections)
  }, [])

  return (
    <>
      <JsonLdScript
        schema={formatReviewToSchema(content, `${latestPublishDate}`, {
          pros: prosList,
          cons: consList,
        })}
        tagId="review"
      />
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle="Reviews - cinch"
        fallbackDescription="We're the faff-free car service that puts you in the driving seat. We help you find and buy used cars – quickly and easily. Buy online with home delivery or from an approved dealer."
      />

      <article className={styles.page}>
        {gallery && <GalleryModal images={images} />}

        {!IS_MOBILE_APP ? (
          <div className={styles.breadcrumbSection}>
            <div className={styles.breadcrumbWrapper}>
              <Breadcrumbs
                breadcrumbs={[
                  { text: 'Home', url: '/' },
                  { text: 'Reviews', url: '/reviews' },
                  { text: title, url: '' },
                ]}
              />
            </div>
          </div>
        ) : null}

        {validationErrors && validationErrors.length > 0 ? (
          <ErrorModal validationErrors={validationErrors} />
        ) : null}

        <div className={styles.sectionWrapper}>
          {introImage && latestPublishDate && (
            <Intro
              contentTitle={title}
              contentStarRating={contentStarRating}
              contentIntroText={contentIntroText}
              authorName={authorName}
              authorImage={authorImage}
              imageCount={imageCount}
              introImage={introImage}
              latestPublishDate={latestPublishDate}
              video={video}
            />
          )}
        </div>

        <div className={styles.prosAndConsWrapper}>
          <ProsAndCons
            prosList={prosList}
            consList={consList}
            make={make}
            model={model}
            areVehiclesInStock={areVehiclesInStock}
          />
        </div>

        <ReviewSections
          make={make}
          model={model}
          reviewSections={reviewSections}
          imageCount={imageCount}
          placeholderImage={placeholderImage}
          areVehiclesInStock={areVehiclesInStock}
        >
          <div className={styles.verdictWrapper}>
            <Verdict
              title={verdictTitle}
              body={verdictBody}
              starRating={contentStarRating}
              make={make}
              model={model}
              reviewSections={reviewSections}
              areVehiclesInStock={areVehiclesInStock}
            />
          </div>
        </ReviewSections>
        {shouldShowLatestCars && (
          <div className={styles.sectionWrapper}>
            <LatestCars make={make} model={model} />
          </div>
        )}
        {relatedReviews.length > 0 && (
          <div className={styles.sectionWrapper}>
            <RelatedReviews make={make} model={model} items={relatedReviews} />
          </div>
        )}
        <div className={styles.sectionWrapper}>
          <CtaPreFooter />
        </div>
      </article>
    </>
  )
}

export default CarReviewsV2Feature
